import {
	forwardRef,
	ForwardRefExoticComponent,
	ReactElement,
	Ref,
	RefAttributes
} from "react";
import { AppType } from "client/Api/Apps/common";
import { EditAzureApp } from "./Apps/EditAzureApp";
import { EditAfasApp } from "./Apps/EditAfasApp";
import { EditEmbedApp } from "./Apps/EditEmbedApp";
import { EditRssApp } from "./Apps/EditRssApp";
import { EditShortcutApp } from "./Apps/EditShortcutApp";
import { EditCalendarApp } from "./Apps/EditCalendarApp";
import { EditCitrixXenappApp } from "./Apps/EditCitrixXenappApp";
import { EditAutotaskApp } from "./Apps/EditAutotaskApp";
import { EditGoogleApp } from "./Apps/EditGoogleApp";
import { EditNedapOnsApp } from "./Apps/EditNedapOnsApp";
import { EditEmailApp } from "./Apps/EditEmailApp";
import { EditDocumentsApp } from "./Apps/EditDocumentsApp";
import { EditConnectWiseApp } from "./Apps/EditConnectWiseApp";
import { EditClientlessRdpApp } from "./Apps/EditClientlessRdpApp";
import { EditZaaksysteemApp } from "./Apps/EditZaaksysteemApp";
import { EditToDoApp } from "./Apps/EditToDoApp";
import { EditZenyaDocApp } from "./Apps/EditZenyaDocApp";
import { EditLiquitApp } from "./Apps/EditLiquitApp";
import { EditLocalApp } from "./Apps/EditLocalApp";
import { EditSharePointApp } from "./Apps/EditSharePointApp";
import { EditTeamsApp } from "./Apps/EditTeamsApp";
import { EditVilansKickApp } from "./Apps/EditVilansKickApp";
import { EditTopDeskApp } from "./Apps/EditTopDeskApp";
import { EditVmwareHorizonApp } from "./Apps/EditVmwareHorizonApp";
import { EditWindowsVirtualDesktopApp } from "./Apps/EditWindowsVirtualDesktopApp";
import { EditZermeloApp } from "./Apps/EditZermeloApp";
import { EditGenericApp } from "./Apps/EditGenericApp";
import { EditAppSettings } from "./EditAppBase";
import { EditGenericAppViewModel } from "client/Api/Apps/EditApp/saveApp";
import { EditAppSavedResult } from "./useSaveApp";
import { EditMagisterApp } from "./Apps/EditMagisterApp";
import { EditAddressBookApp } from "./Apps/EditAddressbookApp";
import { EditIntegrationApp } from "./Apps/EditIntegrationApp";
import { EditBirthdayApp } from "./Apps/EditBirthdayApp";

export type EditAppHandle = {
	save(): Promise<EditAppSavedResult>;
};

export type EditAppProps = {
	initialSettings: EditAppSettings<EditGenericAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditApp = forwardRef(
	(props: EditAppProps, ref: Ref<EditAppHandle>): ReactElement => {
		const {
			initialSettings
		} = props;

		const appKind: AppType = initialSettings.model
			.app
			.type;

		const Component: ImplementationComponent | null = kindToImplementationMap[appKind];

		if (!Component) {
			throw new Error(`'${appKind}' is not supported`);
		}

		return (
			<Component
				{...props as any}
				ref={ref}
			/>
		);
	}
);
EditApp.displayName = "EditApp";

type ImplementationComponent = ForwardRefExoticComponent<any & RefAttributes<EditAppHandle>>;

const kindToImplementationMap: { [key in AppType]: ImplementationComponent | null; } = {
	[AppType.Unknown]: null,
	[AppType.Url]: EditShortcutApp,
	[AppType.Email]: EditEmailApp,
	[AppType.Documents]: EditDocumentsApp,
	[AppType.Office365Word]: EditGenericApp,
	[AppType.Office365Excel]: EditGenericApp,
	[AppType.Office365PowerPoint]: EditGenericApp,
	[AppType.Office365OneNote]: EditGenericApp,
	[AppType.Office365LyncMeeting]: EditGenericApp,
	[AppType.Office365PeopleScreen]: EditGenericApp,
	[AppType.Office365Calendar]: EditCalendarApp,
	[AppType.CitrixXenApp]: EditCitrixXenappApp,
	[AppType.Rss]: EditRssApp,
	[AppType.AzureApp]: EditAzureApp,
	[AppType.ClientlessRdp]: EditClientlessRdpApp,
	[AppType.PowerBi]: EditGenericApp,
	[AppType.Yammer]: EditGenericApp,
	[AppType.Embed]: EditEmbedApp,
	[AppType.Google]: EditGoogleApp,
	[AppType.ToDo]: EditToDoApp,
	[AppType.TopDesk]: EditTopDeskApp,
	[AppType.ConnectWise]: EditConnectWiseApp,
	[AppType.Teams]: EditTeamsApp,
	[AppType.SharePoint]: EditSharePointApp,
	[AppType.AddressBook]: EditAddressBookApp,
	[AppType.HowTo]: EditGenericApp,
	[AppType.Hub]: EditGenericApp,
	[AppType.LocalApp]: EditLocalApp,
	[AppType.WindowsVirtualDesktop]: EditWindowsVirtualDesktopApp,
	[AppType.VilansKick]: EditVilansKickApp,
	[AppType.Birthday]: EditBirthdayApp,
	[AppType.Autotask]: EditAutotaskApp,
	[AppType.YouTube]: EditGenericApp,
	[AppType.NedapOns]: EditNedapOnsApp,
	[AppType.Afas]: EditAfasApp,
	[AppType.Zaaksysteem]: EditZaaksysteemApp,
	[AppType.Zermelo]: EditZermeloApp,
	[AppType.ZenyaDoc]: EditZenyaDocApp,
	[AppType.Liquit]: EditLiquitApp,
	[AppType.VmwareHorizonApp]: EditVmwareHorizonApp,
	[AppType.Magister]: EditMagisterApp,
	[AppType.Integration]: EditIntegrationApp
};
